// Frame statistics and adaptive pixel ratio.
//
// requestAnimationFrame runs at the display refresh even when nothing is rendered (render on demand), so
// idle frames give a clean estimate of the refresh interval. While frames are rendered back to back, a
// mean interval well above the refresh interval means frames are being dropped: lower the pixel ratio.
// When frames have fit the budget for a while, raise it again, but not back to a level that failed recently.

const RING = 120;

export class FrameStats {
  private intervals = new Float32Array(RING);
  private cpu = new Float32Array(RING);
  private n = 0;
  private i = 0;
  private renderedTimes = new Float64Array(RING);
  private ri = 0;

  /** Record one rendered frame: interval since the previous rAF, CPU time spent in update+render. */
  rendered(now: number, intervalMs: number, cpuMs: number): void {
    this.intervals[this.i] = intervalMs;
    this.cpu[this.i] = cpuMs;
    this.i = (this.i + 1) % RING;
    if (this.n < RING) this.n++;
    this.renderedTimes[this.ri] = now;
    this.ri = (this.ri + 1) % RING;
  }

  /** Rendered frames during the last second. */
  fps(now: number): number {
    let c = 0;
    for (let k = 0; k < RING; k++) if (now - this.renderedTimes[k]! < 1000) c++;
    return c;
  }

  meanInterval(count = 30): number {
    return this.mean(this.intervals, count);
  }

  meanCpu(count = 30): number {
    return this.mean(this.cpu, count);
  }

  private mean(a: Float32Array, count: number): number {
    const m = Math.min(count, this.n);
    if (!m) return 0;
    let s = 0;
    for (let k = 1; k <= m; k++) s += a[(this.i - k + RING) % RING]!;
    return s / m;
  }
}

export class RefreshEstimator {
  private buf = new Float32Array(90);
  private n = 0;
  private i = 0;
  private sorted = new Float32Array(90);
  private cached = 1000 / 60;
  private dirty = 0;

  add(intervalMs: number): void {
    if (!(intervalMs > 2 && intervalMs < 100)) return;
    this.buf[this.i] = intervalMs;
    this.i = (this.i + 1) % this.buf.length;
    if (this.n < this.buf.length) this.n++;
    if (++this.dirty >= 30) this.recompute();
  }

  /** ~20th percentile of recent rAF intervals: the vsync period. */
  get ms(): number {
    return this.cached;
  }

  private recompute(): void {
    this.dirty = 0;
    const s = this.sorted.subarray(0, this.n);
    s.set(this.buf.subarray(0, this.n));
    s.sort();
    const v = s[Math.floor(this.n * 0.2)]!;
    // snap to common refresh rates to avoid drifting estimates
    let best = v, bestErr = Infinity;
    for (const hz of [30, 48, 50, 60, 72, 75, 90, 100, 120, 144, 165, 240]) {
      const ms = 1000 / hz, err = Math.abs(ms - v) / ms;
      if (err < bestErr) { bestErr = err; best = ms; }
    }
    this.cached = bestErr < 0.08 ? best : v;
  }
}

export interface AdaptiveOptions {
  min: number;
  max: number;
  initial: number;
  onChange: (dpr: number) => void;
}

/**
 * Pixel ratio controller. Lowers the ratio only when frames miss the refresh AND the GPU is the reason:
 * with a GPU timer, the measured GPU time must be over 70% of the frame budget; without one, the frame's CPU
 * time must be small (a frame that is late because of JavaScript, GC, a texture upload or shader compile is not
 * helped by fewer pixels). Raises it after 2.5 s of on-time frames when the GPU time (if known) leaves room
 * for 21% more pixels, never back to a level that failed in the last 30 s.
 */
export class AdaptiveResolution {
  dpr: number;
  private win = new Float32Array(24); // intervals of the current evaluation window
  private cpuWin = new Float32Array(24);
  private count = 0;
  private lastChange = 0;
  private failCeiling = Infinity;
  private failAt = 0;
  private goodSince = 0;
  private holdUntil = 0;
  enabled = true;
  /** Counters for the HUD and tests. */
  readonly stats = { lowered: 0, raised: 0, cpuBound: 0 };

  constructor(private opts: AdaptiveOptions) {
    this.dpr = clamp(opts.initial, opts.min, opts.max);
  }

  /** Ignore frames until `now + ms` (loading, entering, resizing: uploads and compiles make frames late). */
  hold(now: number, ms: number): void {
    this.holdUntil = Math.max(this.holdUntil, now + ms);
    this.count = 0;
    this.goodSince = 0;
  }

  /**
   * Call every rAF. `rendered`: a frame was drawn this rAF; `prevRendered`: the previous rAF drew too.
   * `cpuMs`: JS time of this frame; `gpuMs`: recent GPU time per frame, NaN if unknown.
   */
  frame(now: number, intervalMs: number, rendered: boolean, prevRendered: boolean, refreshMs: number, cpuMs: number, gpuMs: number): void {
    if (!this.enabled) return;
    if (!rendered || !prevRendered || now < this.holdUntil) {
      this.count = 0; this.goodSince = 0;
      return;
    }
    this.cpuWin[this.count] = cpuMs;
    this.win[this.count++] = intervalMs;
    if (this.failCeiling !== Infinity && now - this.failAt > 30000) this.failCeiling = Infinity;
    if (this.count < this.win.length) return;
    // trimmed mean: drop the two longest intervals so a single hitch does not count as pressure, but
    // alternating slow/fast frames still do
    this.win.sort();
    let sum = 0;
    for (let k = 0; k < this.win.length - 2; k++) sum += this.win[k]!;
    const mean = sum / (this.win.length - 2);
    let cpu = 0;
    for (let k = 0; k < this.cpuWin.length; k++) cpu += this.cpuWin[k]!;
    cpu /= this.cpuWin.length;
    this.count = 0;
    const gpuKnown = Number.isFinite(gpuMs);
    if (mean > refreshMs * 1.3 && now - this.lastChange > 700) {
      const gpuBound = gpuKnown ? gpuMs > refreshMs * 0.7 : cpu < refreshMs * 0.4;
      if (gpuBound && this.dpr > this.opts.min) {
        this.failCeiling = this.dpr;
        this.failAt = now;
        this.set(this.dpr * 0.85, now);
        this.stats.lowered++;
      } else if (!gpuBound) this.stats.cpuBound++;
      this.goodSince = 0;
    } else if (mean < refreshMs * 1.08) {
      if (!this.goodSince) this.goodSince = now;
      const next = this.dpr * 1.1;
      const room = !gpuKnown || gpuMs * 1.21 < refreshMs * 0.6;
      if (room && now - this.goodSince > 2500 && now - this.lastChange > 2500 && this.dpr < this.opts.max && next < this.failCeiling * 0.98) {
        this.set(next, now);
        this.goodSince = now;
        this.stats.raised++;
      }
    } else {
      this.goodSince = 0;
    }
  }

  private set(v: number, now: number): void {
    const q = Math.round(clamp(v, this.opts.min, this.opts.max) * 20) / 20;
    if (q === this.dpr) return;
    this.dpr = q;
    this.lastChange = now;
    this.opts.onChange(q);
  }
}

function clamp(v: number, a: number, b: number): number {
  return v < a ? a : v > b ? b : v;
}
